import { useQuery } from '@tanstack/react-query';

import { useAuthStore } from '../store/auth.store';
import { getCurrentUser } from './auth.api';

/**
 * The signed-in user — identity, role and permissions — as server state.
 *
 * This query is the ONLY place permissions live in the client. It is:
 *  - seeded at login (so the first screen renders without a second round trip),
 *  - fetched on every page load after the session is restored,
 *  - refreshed periodically and on window focus, so a role change or
 *    deactivation made by an administrator reaches an open tab without the
 *    user signing out and back in.
 *
 * A 401 goes through the client's refresh-and-retry path; if that fails the
 * session is cleared and the guards send the user to the login page.
 */
export const currentUserKeys = {
  me: ['auth', 'me'] as const,
};

const FIVE_MINUTES = 5 * 60_000;

export function useCurrentUser() {
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');

  return useQuery({
    queryKey: currentUserKeys.me,
    queryFn: () => getCurrentUser(),
    enabled: isAuthenticated,
    staleTime: FIVE_MINUTES,
    refetchInterval: FIVE_MINUTES,
    refetchOnWindowFocus: true,
    // 401s are handled by the client (refresh, then sign-out); retrying the
    // query on top of that only delays the outcome.
    retry: false,
  });
}
